import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '../Modal';
import { ModalFooter } from '../ModalFooter';
import { ToggleSwitch } from '../ToggleSwitch';
import { FormGroup } from '../FormGroup';
import { UploadSource } from '../UploadSource';
import { getConfigService, getTokenService } from '../../services/angular-bridge';
import { notifySuccess, notifyError } from '../../services/notify';

interface Props {
  projectId: string;
  collectionId: string;
  resourceId: string;
  projectName?: string;
  onClose: () => void;
  // Portal target — the detached pop-out window's document when the viewer is detached.
  targetDocument?: Document;
}

/**
 * Replace PDF — React port of ReplacePdfModalController / dialog_replacepdf.html. Uploads
 * a replacement PDF for the resource (raw XHR for upload progress, matching the load-file
 * upload pattern), with an "is redacted" flag. The file picker uses the shared UploadSource
 * drop target (Angular's iio-drop-target). On success the resource + its rendered PDF are
 * invalidated so the viewer reloads the new document.
 */
export function ReplacePdfModal({ projectId, collectionId, resourceId, projectName, onClose, targetDocument }: Props) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [isRedacted, setIsRedacted] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [bytesLoaded, setBytesLoaded] = useState(0);
  const [bytesTotal, setBytesTotal] = useState(0);
  const xhrRef = useRef<XMLHttpRequest | null>(null);

  const submit = () => {
    if (!file || uploading) return;
    const base = getConfigService().getServerBaseUrl();
    const url = `${base}/project/${projectId}/collection/${collectionId}/resource/${resourceId}/pdfreplace?isRedacted=${isRedacted}`;
    const formData = new FormData();
    formData.append('file', file);

    const xhr = new XMLHttpRequest();
    xhrRef.current = xhr;
    setUploading(true);
    setProgress(0);
    setBytesLoaded(0);
    setBytesTotal(0);
    xhr.upload.addEventListener('progress', (ev) => {
      if (ev.lengthComputable) {
        setBytesLoaded(ev.loaded);
        setBytesTotal(ev.total);
        setProgress(Math.floor((ev.loaded * 100) / ev.total));
      }
    });
    xhr.addEventListener('load', () => {
      setUploading(false);
      if (xhr.status >= 200 && xhr.status < 300) {
        notifySuccess({ title: 'PDF replaced', description: 'The replacement PDF is being processed.', timeout: 5000 });
        queryClient.invalidateQueries({ queryKey: ['resource', projectId, collectionId, resourceId] });
        queryClient.invalidateQueries({ queryKey: ['resource-pdf', projectId, collectionId, resourceId] });
        onClose();
      } else {
        notifyError({ title: 'Replace PDF failed', description: 'Please try again later.', timeout: 5000 });
      }
    });
    xhr.addEventListener('error', () => {
      setUploading(false);
      notifyError({ title: 'Replace PDF failed', description: 'Please try again later.', timeout: 5000 });
    });
    xhr.open('POST', url);
    const token = getTokenService().GetAccessToken?.();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.send(formData);
  };

  return (
    <Modal
      title="Replace PDF"
      icon="far fa-file-pdf"
      onClose={onClose}
      targetDocument={targetDocument}
      testId="viewer-replace-pdf-modal"
      onSubmit={(e) => { e.preventDefault(); submit(); }}
      footer={
        <ModalFooter
          onClose={onClose}
          nextLabel="Replace"
          nextDisabled={!file || uploading}
          saving={uploading}
          savingLabel="Replacing"
        />
      }
    >
      {projectName && (
        <h4>Project: <small><i className="fas fa-gavel" aria-hidden="true" /> {projectName}</small></h4>
      )}
      <FormGroup label="Replacement PDF is Redacted" labelCols="col-sm-6" inputCols="col-sm-6" labelAlign="middle">
        <ToggleSwitch checked={isRedacted} onChange={setIsRedacted} disabled={uploading} onLabel="YES" offLabel="NO" size="small" testId="viewer-replace-pdf-redacted-toggle" />
      </FormGroup>
      {/* No label — the drop target fills the full modal width (matches Angular). */}
      <div className="form-group" data-testid="viewer-replace-pdf-file-panel">
        <UploadSource
          file={file}
          onChange={setFile}
          accept="application/pdf,.pdf"
          disabled={uploading}
          uploading={uploading}
          uploadProgress={progress}
          uploadBytesLoaded={bytesLoaded}
          uploadBytesTotal={bytesTotal}
          testId="viewer-replace-pdf-upload-panel"
        />
      </div>
    </Modal>
  );
}
